// src/components/Tasks.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Container,
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    TextField,
    Grid,
    MenuItem,
    IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
const API_URL = process.env.REACT_APP_API_URL || '';

// Komponent pobierający użytkowników do przypisywania
const UsersSelect = ({ value, onChange }) => {
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem('token');
                // Pobieramy listę użytkowników z endpointu assignable-users
                const response = await axios.get(`${API_URL}/api/assignable-users`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUsers(response.data);
            } catch (error) {
                console.error('Error fetching assignable users:', error);
            }
        };
        fetchUsers();
    }, []);

    return (
        <TextField
            select
            fullWidth
            label="Przydzielony do"
            name="assigned_to"
            value={value}
            onChange={onChange}
            required
        >
            {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                    {user.username} ({user.email})
                </MenuItem>
            ))}
        </TextField>
    );
};

const Tasks = () => {
    const [tasks, setTasks] = useState([]);
    const [form, setForm] = useState({
        title: '',
        description: '',
        service_type: '',
        priority: '',
        status: '',
        due_date: '',
        assigned_to: ''
    });
    const token = localStorage.getItem('token');
    const navigate = useNavigate();

    const fetchTasks = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/tasks`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTasks(response.data);
        } catch (error) {
            console.error('Error fetching tasks:', error);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, [token]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/tasks`, form, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchTasks();
            setForm({
                title: '',
                description: '',
                service_type: '',
                priority: '',
                status: '',
                due_date: '',
                assigned_to: ''
            });
        } catch (error) {
            console.error('Error adding task:', error);
        }
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Zadania
                </Typography>
                <Paper sx={{ p: 2, mb: 4 }}>
                    <Typography variant="h6">Dodaj nowe zadanie</Typography>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Tytuł"
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Opis"
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Typ usługi"
                                    name="service_type"
                                    value={form.service_type}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Priorytet"
                                    name="priority"
                                    value={form.priority}
                                    onChange={handleChange}
                                    required
                                >
                                    {[1, 2, 3, 4, 5].map(num => (
                                        <MenuItem key={num} value={num}>
                                            {num}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Status"
                                    name="status"
                                    value={form.status}
                                    onChange={handleChange}
                                    required
                                >
                                    <MenuItem value="pending">pending</MenuItem>
                                    <MenuItem value="in_progress">in_progress</MenuItem>
                                    <MenuItem value="completed">completed</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Data wykonania"
                                    name="due_date"
                                    type="date"
                                    value={form.due_date}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <UsersSelect
                                    value={form.assigned_to}
                                    onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" color="primary">
                                    Dodaj zadanie
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Lista zadań
                    </Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Tytuł</TableCell>
                                <TableCell>Opis</TableCell>
                                <TableCell>Typ usługi</TableCell>
                                <TableCell>Priorytet</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Data wykonania</TableCell>
                                <TableCell>Przydzielony do</TableCell>
                                <TableCell>Utworzony</TableCell>
                                <TableCell>Akcje</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tasks.map(task => (
                                <TableRow key={task.id}>
                                    <TableCell>{task.id}</TableCell>
                                    <TableCell>{task.title}</TableCell>
                                    <TableCell>{task.description}</TableCell>
                                    <TableCell>{task.service_type}</TableCell>
                                    <TableCell>{task.priority}</TableCell>
                                    <TableCell>{task.status}</TableCell>
                                    <TableCell>{task.due_date}</TableCell>
                                    <TableCell>
                                        {task.assigned_username ? (
                                            <>
                                                {task.assigned_username} <br />
                                                <small>{task.assigned_email}</small>
                                            </>
                                        ) : (
                                            'Nie przypisano'
                                        )}
                                    </TableCell>
                                    <TableCell>{new Date(task.created_at).toLocaleString()}</TableCell>
                                    <TableCell>
                                        <IconButton color="primary" onClick={() => navigate(`/tasks/edit/${task.id}`)}>
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            </Box>
        </Container>
    );
};

export default Tasks;